import * as THREE from 'three'
import { useLayoutEffect, useMemo, useRef, useState, useEffect } from 'react'
import { Canvas, extend, useFrame } from '@react-three/fiber'
import { Image, ScrollControls, useScroll, Billboard, Text } from '@react-three/drei'
import { suspend } from 'suspend-react'
import { easing, geometry } from 'maath'
import Iframe from 'react-iframe'
import { proxy, useSnapshot } from 'valtio'
import { motion, AnimatePresence } from 'framer-motion'

extend(geometry)
const inter = import('@pmndrs/assets/fonts/inter_regular.woff')
const amount = 102

const state = proxy({
  selected: false,
  selectedIndex: 0,
  animationUrl: ''
})

export const App = () => {
  const metadata = useMemo(() => {
    let temp = []
    Array.from({ length: amount }, (_, i) => {
      fetch(`/json/${i + 1}`)
        .then(response => response.json())
        .then(data => temp[i] = data)
    })
    return temp
  }, [])

  return (
    <>
      <Canvas dpr={[1, 1.5]}>
        <ScrollControls pages={4} infinite>
          <Scene position={[0, 1.5, 0]} metadata={metadata} />
        </ScrollControls>
      </Canvas>
      <Squish />
    </>
  )
}

const Squish = () => {
  const snap = useSnapshot(state)
  return (
    <AnimatePresence>
      {snap.selected && (
        <motion.div
          style={{ position: 'absolute', top: 0, left: 0, width: '100vw', height: '100vh' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ type: 'spring', duration: 1 }}
        >
          <Iframe url={snap.animationUrl}
            width="100%"
            height="100%"
            className=""
            display="block"
            frameBorder={0}
            position="relative" />
          <motion.button
            className="btn"
            style={{ left: 10 }}
            onClick={() => (state.selected = false)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ type: 'spring', duration: 0.8, delay: 3 }}
          >
            BACK
          </motion.button>
          <motion.button
            className="btn"
            style={{ left: 100 }}
            onClick={() => (window.open(`https://opensea.io/assets/base/0x0ddfe6192282b96d545366bb7a81075e60a0fbb1/${snap.selectedIndex}`, '_blank', 'noopener,noreferrer'))}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ type: 'spring', duration: 0.8, delay: 3 }}
          >
            OPEN IN OPENSEA
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function Scene({ children, metadata, ...props }) {
  const ref = useRef()
  const scroll = useScroll()
  const [hovered, hover] = useState(null)
  const [clicked, click] = useState(false)

  useEffect(() => {
    if (clicked) {
      const timeoutId = setTimeout(() => click(false), 1000)
      return () => clearTimeout(timeoutId)
    }
  }, [clicked])

  useFrame((state, delta) => {
    ref.current.rotation.y = -scroll.offset * (Math.PI * 2)
    state.events.update()
    easing.damp3(state.camera.position, [-state.pointer.x * 2, state.pointer.y * 2 + 4.5, 9], 0.3, delta)
    state.camera.lookAt(0, 0, 0)
  })
  return (
    <group ref={ref} {...props}>
      <Cards category="Da Squish" from={0} onPointerOver={hover} onPointerOut={hover} metadata={metadata} onCardClick={click} />
      <ActiveCard hovered={hovered} clicked={clicked} />
    </group>
  )
}

function Cards({ category, data, from = 0, len = Math.PI * 2, radius = 6.25, onPointerOver, onPointerOut, metadata, onCardClick, ...props }) {
  const [hovered, hover] = useState(null)
  const textPosition = from + (amount / 2 / amount) * len

  return (
    <group {...props}>
      {/* <Billboard position={[Math.sin(textPosition) * radius * 1.4, 0.5, Math.cos(textPosition) * radius * 1.4]}>
        <Text font={suspend(inter).default} fontSize={0.25} anchorX="center" color="black">
          {category}
        </Text>
      </Billboard> */}
      {Array.from({ length: amount - 3 /* minus 3 images at the end, creates a gap */ }, (_, i) => {
        const angle = from + (i / amount) * len
        return (
          <Card
            key={angle}
            onPointerOver={(e) => (e.stopPropagation(), hover(i), onPointerOver(i))}
            onPointerOut={() => (hover(null), onPointerOut(null))}
            position={[Math.sin(angle) * radius, 0, Math.cos(angle) * radius]}
            rotation={[0, Math.PI / 2 + angle, 0]}
            active={hovered !== null}
            hovered={hovered === i}
            url={`/media/${Math.floor(i % 1000) + 1}.jpg`}
            metadata={metadata[i]}
            index={i + 1}
            onCardClick={onCardClick}
          />
        )
      })}
    </group>
  )
}

function Card({ url, active, hovered, metadata, index, onCardClick, ...props }) {
  const ref = useRef()
  useFrame((state, delta) => {
    const f = hovered ? 1.4 : active ? 1.25 : 1
    easing.damp3(ref.current.position, [0, hovered ? 0.25 : 0, 0], 0.1, delta)
    easing.damp3(ref.current.scale, [1.618 * f, 1 * f, 1], 0.15, delta)
  })

  const handleClick = () => {
    if (hovered && metadata && metadata.animation_url) {
      state.animationUrl = metadata.animation_url
      state.selectedIndex = index
      state.selected = true
      // window.open(metadata.animation_url, '_blank', 'noopener,noreferrer')
      onCardClick(true)
    }
  }
  return (
    <group {...props}>
      <Image ref={ref} transparent radius={0.075} url={url} scale={[1.618, 1, 1]} side={THREE.DoubleSide} onClick={handleClick} />
    </group>
  )
}

function ActiveCard({ hovered, clicked, ...props }) {
  const ref = useRef()
  useLayoutEffect(() => void (ref.current.material.zoom = 0.8), [hovered])
  useFrame((state, delta) => {
    const f = clicked ? 10 : 5
    easing.damp(ref.current.material, 'zoom', 1, 0.5, delta)
    easing.damp(ref.current.material, 'opacity', hovered !== null, 0.3, delta)
    easing.damp3(ref.current.scale, [f, f], 0.1, delta)
  })
  return (
    <Billboard {...props}>
      <Text font={suspend(inter).default} fontSize={0.5} position={[3, 3.85, 0]} anchorX="left" color="black">
        {hovered !== null && `Da Squish ${hovered + 1}`}
      </Text>
      <Image ref={ref} transparent radius={0.3} position={[0, 1.5, 0]} scale={[5, 5, 0.2, 1]} url={`/media/${Math.floor(hovered % 1000) + 1}.jpg`} />
    </Billboard>
  )
}
